import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import '../../assets/css/imageeffects.css';

// Image Effects Panel - add effects like brightness, contrast, saturation
const ImageEffectsPanel = ({
  imageId,
  item,
  disabled = false,
  onUpdate,
}) => {
  const [showEffects, setShowEffects] = useState(false);

  if (!item) return null;

  // Effect state from item or defaults
  const brightness = item.brightness ?? 100;
  const contrast = item.contrast ?? 100;
  const saturation = item.saturation ?? 100;
  const grayscale = item.grayscale ?? 0;
  const sepia = item.sepia ?? 0;
  const hueRotate = item.hueRotate ?? 0;
  const blur = item.blur ?? 0;

  const handleEffectChange = (effect, value) => {
    onUpdate(imageId, effect, Number(value));
  };

  const handleResetEffects = () => {
    onUpdate(imageId, 'brightness', 100);
    onUpdate(imageId, 'contrast', 100);
    onUpdate(imageId, 'saturation', 100);
    onUpdate(imageId, 'grayscale', 0);
    onUpdate(imageId, 'sepia', 0);
    onUpdate(imageId, 'hueRotate', 0);
    onUpdate(imageId, 'blur', 0);
  };

  // Compute the CSS filter string for preview
  const filterString = `
    brightness(${brightness}%)
    contrast(${contrast}%)
    saturate(${saturation}%)
    grayscale(${grayscale}%)
    sepia(${sepia}%)
    hue-rotate(${hueRotate}deg)
    blur(${blur}px)
  `.trim();

  return (
    <div className="image-effects-panel">
      <div className="effects-header">
        <h4>Image Effects</h4>
        <button
          type="button"
          className="toggle-button"
          disabled={disabled}
          title="Reset all effects"
          onClick={handleResetEffects}
        >
          <RotateCcw size={14} color="#80808082" />
        </button>
      </div>

      {/* Brightness */}
      <div className="effect-group">
        <div className="effect-label-row">
          <label>Brightness</label>
          <span className="effect-value">{brightness}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="200"
          step="5"
          value={brightness}
          disabled={disabled}
          onChange={(e) => handleEffectChange('brightness', e.target.value)}
          className="effect-slider"
        />
      </div>

      {/* Contrast */}
      <div className="effect-group">
        <div className="effect-label-row">
          <label>Contrast</label>
          <span className="effect-value">{contrast}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="200"
          step="5"
          value={contrast}
          disabled={disabled}
          onChange={(e) => handleEffectChange('contrast', e.target.value)}
          className="effect-slider"
        />
      </div>

      {/* Saturation */}
      <div className="effect-group">
        <div className="effect-label-row">
          <label>Saturation</label>
          <span className="effect-value">{saturation}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="200"
          step="5"
          value={saturation}
          disabled={disabled}
          onChange={(e) => handleEffectChange('saturation', e.target.value)}
          className="effect-slider"
        />
      </div>

      {/* Grayscale */}
      <div className="effect-group">
        <div className="effect-label-row">
          <label>Grayscale</label>
          <span className="effect-value">{grayscale}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          value={grayscale}
          disabled={disabled}
          onChange={(e) => handleEffectChange('grayscale', e.target.value)}
          className="effect-slider"
        />
      </div>

      {/* Sepia */}
      <div className="effect-group">
        <div className="effect-label-row">
          <label>Sepia</label>
          <span className="effect-value">{sepia}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          value={sepia}
          disabled={disabled}
          onChange={(e) => handleEffectChange('sepia', e.target.value)}
          className="effect-slider"
        />
      </div>

      {/* Hue Rotate */}
      <div className="effect-group">
        <div className="effect-label-row">
          <label>Hue Rotate</label>
          <span className="effect-value">{hueRotate}°</span>
        </div>
        <input
          type="range"
          min="0"
          max="360"
          step="5"
          value={hueRotate}
          disabled={disabled}
          onChange={(e) => handleEffectChange('hueRotate', e.target.value)}
          className="effect-slider"
        />
      </div>

      {/* Blur */}
      <div className="effect-group">
        <div className="effect-label-row">
          <label>Blur</label>
          <span className="effect-value">{blur}px</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={blur}
          disabled={disabled}
          onChange={(e) => handleEffectChange('blur', e.target.value)}
          className="effect-slider"
        />
      </div>
    </div>
  );
};

export default ImageEffectsPanel;
